import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Zap, Wifi, Clock, Target, Battery, Database } from 'lucide-react';
import { heroStats, images } from '../data/mock';
import { useMode } from '../contexts/ModeContext';

const Hero = () => {
  const { mode } = useMode();
  const iconMap = {
    zap: Zap,
    wifi: Wifi,
    clock: Clock,
    target: Target,
    battery: Battery,
    database: Database
  };

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex flex-col justify-between overflow-hidden pt-32 pb-0">
      {/* Animated Background */}
      <div className="absolute inset-0 z-0">
        <img
          src={images.hero}
          alt="Futuristic Technology"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/75 via-slate-900/45 to-slate-900/75" />
        
        {/* Animated Matrix Overlay */}
        <div className="absolute inset-0 opacity-20">
          {Array.from({ length: 20 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 bg-gradient-to-b from-cyan-400 to-transparent"
              style={{
                left: `${Math.random() * 100}%`,
                height: `${Math.random() * 200 + 100}px`
              }}
              animate={{
                y: [-100, window.innerHeight + 100],
                opacity: [0, 1, 0]
              }}
              transition={
                {
                  duration: Math.random() * 3 + 2,
                  repeat: Infinity,
                  delay: Math.random() * 2
                }
              }
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            {mode === 'solve' ? (
              <>
                Make <span className="text-cyan-400">AI</span> &{' '}
                <span className="text-blue-400">Media</span> Lighter.
                <br />
                Unleash their power with <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">IDDO</span>.
              </>
            ) : (
              <>
                Invest in the <span className="text-purple-400">Future</span>
                <br />
                of <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Data Intelligence</span>
              </>
            )}
          </h1>
          
          <motion.p
            className="text-xl md:text-2xl text-gray-300 mb-6 max-w-4xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            {mode === 'solve' ? (
              <>
                Restructures information to shrink working sets, bandwidth, and power. Maximizing value per byte. Development of novel Intelligent Data Density Optimization solutions powered by patented <span className="text-blue-400 font-semibold">MSML</span>
              </>
            ) : (
              <>
                <span className="text-purple-400 font-semibold">Patented MSML technology</span> positioned at the intersection of multiple 
                high-growth markets. Demos → Targets → Pilots → Revenue.
              </>
            )}
          </motion.p>

          <motion.p
            className="text-lg text-gray-400 mb-8 max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            {mode === 'solve' ? (
              <>
                <span className="text-cyan-400 font-semibold">Advanced R&D.</span> Media demos today. LLM and edge validation next.
              </>
            ) : (
              'TSXV path | IP-first platform | $586B+ TAM'
            )}
          </motion.p>

          {/* Value Proposition Tagline */}
          <motion.p
            className="text-lg md:text-xl text-gray-300 mb-12 max-w-4xl mx-auto border-t border-b border-cyan-400/30 py-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            {mode === 'solve' ? (
              <>
                Maximizing value per byte • <span className="text-cyan-400">$847B total addressable market</span> • Reducing computing loads
              </>
            ) : (
              <>
                Validated patents • Production-ready demos • <span className="text-purple-400">Clear path to revenue</span>
              </>
            )}
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            {mode === 'solve' ? (
              <>
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-lg px-8 py-6 shadow-xl"
                  onClick={() => scrollToSection('#technology')}
                >
                  Explore Technology
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-slate-900 text-lg px-8 py-6"
                  onClick={() => scrollToSection('#contact')}
                >
                  Request IP Brief
                </Button>
              </>
            ) : (
              <>
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-lg px-8 py-6 shadow-xl"
                  onClick={() => scrollToSection('#investment')}
                >
                  View Investor Deck
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-slate-900 text-lg px-8 py-6"
                  onClick={() => scrollToSection('#roadmap')}
                >
                  See Roadmap
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-pink-400 text-pink-400 hover:bg-pink-400 hover:text-slate-900 text-lg px-8 py-6"
                  onClick={() => scrollToSection('#contact')}
                >
                  Join Investor Updates
                </Button>
              </>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* Stats Strip - At Bottom */}
      <motion.div
        className="relative z-10 w-full mt-auto"
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8 }}
      >
        <div className="bg-slate-800/95 backdrop-blur-md border-t-2 border-cyan-400/40 shadow-2xl">
          <div className="container mx-auto px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {heroStats.map((stat, index) => {
                const IconComponent = iconMap[stat.icon];
                return (
                  <motion.div
                    key={stat.label}
                    className="flex items-start gap-4 bg-slate-900/50 rounded-lg p-6 border border-cyan-400/20"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.2 + index * 0.15, duration: 0.5 }}
                  >
                    <div className="flex-shrink-0">
                      <IconComponent className="text-cyan-400 w-8 h-8" />
                    </div>
                    <p className="text-gray-300 text-base leading-relaxed">{stat.label}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;